import React, { useState, useEffect, useRef } from "react";
import logo from "../assets/logo/Logo.svg";
import google from "../assets/logo/Google.svg";
import facebook from "../assets/logo/Facebook.svg";
import apple from "../assets/logo/apple.png";
import Carousel from "./carousel/logincarousel";
import loginbanner from "../assets/images/login-banner.png";
import loginbannertwo from "../assets/images/login-banner-2.png";
import loginbannerthree from "../assets/images/login-banner-3.png";
import loginbannerfour from "../assets/images/login-banner-4.png";
import loginbannersm from "../assets/images/login-banner-sm.png";
import bannerlayout from "../assets/images/banner-layout.png";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
export default function VerifyCodeMobile() {
  const [code, setCode] = useState("");
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const API_URL = `${API_BASE_URL}/auth`;


   // ✅ Handle Google Login
   const handleGoogleLogin = () => {
    window.location.href = `${API_URL}/google`;
  };

  // ✅ Handle Facebook Login
  const handleFacebookLogin = () => {
    window.location.href = `${API_URL}/facebook`;
  };

  // ✅ Resend OTP with 5-minute cooldown
  const [resendTimer, setResendTimer] = useState(0); // seconds remaining
  const [isResending, setIsResending] = useState(false);
  const timerRef = useRef(null);

  const formatTime = (secs) => {
    const mm = String(Math.floor(secs / 60)).padStart(2, "0");
    const ss = String(secs % 60).padStart(2, "0");
    return `${mm}:${ss}`;
  };

  const startResendTimer = (initialSeconds = 300) => {
    // persist expiry so reloads keep the remaining time
    const expiresAt = Date.now() + initialSeconds * 1000;
    sessionStorage.setItem("resendOtpExpiresAt", String(expiresAt));
    setResendTimer(initialSeconds);

    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          sessionStorage.removeItem("resendOtpExpiresAt");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    // On mount, resume timer if there's an existing expiry
    const expires = sessionStorage.getItem("resendOtpExpiresAt");
    if (expires) {
      const expiresAt = parseInt(expires, 10);
      const now = Date.now();
      if (expiresAt > now) {
        const secsLeft = Math.ceil((expiresAt - now) / 1000);
        startResendTimer(secsLeft);
      } else {
        sessionStorage.removeItem("resendOtpExpiresAt");
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleResendOTP = async (e) => {
    e.preventDefault();
    setError(null);

    // Prevent resend while cooling down or already resending
    if (resendTimer > 0 || isResending) return;

    // ✅ Retrieve email from sessionStorage
    const storedEmail = sessionStorage.getItem("userEmail");

    if (!storedEmail) {
      toast.error("No email found. Please sign up again.");
      return;
    }

    setIsResending(true);
    try {
      const response = await fetch(`${API_URL}/forget-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: storedEmail }),
      });

      const data = await response.json();
      console.log("Resend OTP Response:", data);

      if (response.ok && data.success) {
        if (data.result?.data) {
          sessionStorage.setItem("resetToken", data.result.data);
          localStorage.setItem("resetToken", data.result.data);
          console.log("New Reset Token stored:", data.result.data);
        }
        toast.success("A new OTP has been sent to your mobile.");
        setCode("");
        startResendTimer(5 * 60); // 5 minutes
      } else {
        setError(data.message || "Failed to resend OTP. Please try again.");
      }
    } catch (error) {
      console.error("Error:", error);
      setError("Something went wrong. Please try again.");
    } finally {
      setIsResending(false);
    }
  };

  // ✅ Handle OTP Submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent duplicate submissions
    if (isSubmitting) return;
    setIsSubmitting(true);
    setError(null);

    if (resendTimer === 0) {
      setCode("");
      setError("This OTP has expired. Please request a new OTP.");
      setIsSubmitting(false);
      return;
    }

    // Retrieve token from sessionStorage
    const token = sessionStorage.getItem("resetToken");

    if (!token) {
      setIsSubmitting(false);
      setError("Session expired. Please request a new OTP.");
      return;
    }

    try {
      const response = await fetch(`${API_URL}/validate-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, otp: code }),
      });

      const data = await response.json();
      console.log("API Response:", data);

      if (response.ok && data.success) {
        sessionStorage.setItem("otp", data.result?.otp || "");
        localStorage.setItem("otp", data.result?.otp || "");
        toast.success("✅ OTP Verified! Redirecting...");
        navigate("/resetpassword");
      } else {
        // ✅ Show API Error Below OTP Field
        setError(data.message || "Invalid OTP. Please try again.");
      }
    } catch (error) {
      console.error("Error:", error);
      setError("Something went wrong. Please try again.");
    } finally {
      // Re-enable button unless we navigated away
      setIsSubmitting(false);
    }
  };

  return (
    <div className="login">
      <div className="container min-h-screen flex">
        {/* Left Side Image */}
        <div className="hidden lg:block lg:w-1/2">
          <div className="carousel-size pb-3 pt-3">
            <Carousel slides={[loginbanner, loginbannertwo, loginbannerthree, loginbannerfour]} />
            <img src={bannerlayout} alt="Overlay" className="layout" />
          </div>
          <div className="copy-rights py-6 text-center text-sm text-[#095228]">
            © 2025 Pipaan. All Rights Reserved
          </div>
        </div>

        {/* Right Side Form */}
        <div className="w-full lg:w-1/2 login-content flex flex-col">
          <div className="login-form">
            <div className="reset-pass flex flex-col items-center justify-center px-6">
              {/* Logo */}
              <div className="logo">
                <img src={logo} alt="Pipaan Logo" width={150} height={50} />
              </div>

              <div className="w-full max-w-md">
                <h1 className="login-title text-2xl font-semibold text-left mb-2">Verify Code</h1>
                <p className="text-left text-[#313131] text-sm mb-8">
                  An authentication code has been sent to your mobile.
                </p>

                {/* ✅ Verification Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="w-full relative">
                    <input
                      type="text"
                      placeholder="Enter Verification Code"
                      value={code}
                      onChange={(e) => {
                        // Keep only digits and limit to 6 chars
                        const digits = e.target.value.replace(/\D/g, "").slice(0, 6);
                        setCode(digits);
                      }}
                      onKeyDown={(e) => {
                        const allowedKeys = ["Backspace","Delete","Tab","Escape","Enter","ArrowLeft","ArrowRight","ArrowUp","ArrowDown","Home","End"];
                        if (allowedKeys.includes(e.key) || e.ctrlKey || e.metaKey) return;
                        // Block non-digit keys
                        if (!/^\d$/.test(e.key)) {
                          e.preventDefault();
                          return;
                        }
                        // Prevent typing more than 6 digits when there's no selection
                        const target = e.target;
                        const selectionLength = (target.selectionEnd || 0) - (target.selectionStart || 0);
                        if ((target.value.replace(/\D/g, "").length - selectionLength) >= 6) {
                          e.preventDefault();
                        }
                      }}
                      onPaste={(e) => {
                        e.preventDefault();
                        const paste = (e.clipboardData || window.clipboardData).getData("text").replace(/\D/g, "");
                        const target = e.target;
                        const start = target.selectionStart || 0;
                        const end = target.selectionEnd || 0;
                        const pre = code.slice(0, start);
                        const post = code.slice(end);
                        const combined = (pre + paste + post).replace(/\D/g, "").slice(0, 6);
                        setCode(combined);
                      }}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      className="w-full h-12 px-4 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  {/* ✅ Show API Error Below OTP Field */}
                  {error && <p className="text-red-500 text-sm">{error}</p>}

                  <p className="text-lg font-bold text-[#BE1F2E] text-center">
                    {formatTime(resendTimer)}
                  </p>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    aria-busy={isSubmitting}
                    className={`w-full bg-[#1B2537] hover:bg-[#1B2537]/90 text-white py-3 rounded-xl font-medium ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {isSubmitting ? 'Verifying...' : 'Verify'}
                  </button>
                </form>

                <p className="text-sm text-[#313131] text-center">
                  Didn’t receive a code?{" "}
                  <button
                    type="button"
                    className={`text-[#006838] py-3 font-medium ${(!resendTimer && !isResending) ? 'hover:underline' : 'opacity-50 cursor-not-allowed'}`}
                    onClick={handleResendOTP}
                    disabled={isResending || resendTimer > 0}
                    aria-busy={isResending}
                  >
                    {resendTimer > 0 ? `Resend (${formatTime(resendTimer)})` : (isResending ? 'Resending...' : 'Resend')}
                  </button>
                </p>

                {/* Social Login */}
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t-2 border-gray-200"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="bg-white px-2 text-gray-500">Login With</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-10 mx-auto w-fit">
                  <button className="w-full rounded-lg p-2" onClick={handleGoogleLogin}>
                    <img src={google} alt="Google" className="mx-auto" />
                  </button>
                  <button className="w-full rounded-lg p-2" onClick={handleFacebookLogin}>
                    <img src={facebook} alt="Facebook" className="mx-auto" />
                  </button>
                  {/* <button className="w-full rounded-lg p-2">
                    <img src={apple} alt="Apple" className="mx-auto" />
                  </button> */}
                </div>

                {/* Signup Link */}
                <p className="text-center text-sm text-[#313957] mt-4">
                  Don't have an account?{" "}
                  <Link to="/signup" className="text-[#006838] font-semibold hover:underline">
                    Create New Account
                  </Link>
                </p>

                {/* Footer */}
                <div className="lg:hidden">
                  <div className="copy-rights pb-5 text-center text-sm text-[#095228]">
                    © 2025 Pipaan. All Rights Reserved
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
